import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { ApiError } from '@shared/api/client';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { formatNumber } from '@shared/format';
import { ConfirmDialog, type ConfirmSubject } from '@shared/ui/dialog';
import {
  DTable,
  DTableBody,
  DTableHead,
  DTableRowHeader,
  DTableScroll,
  RowSkeleton,
} from '@shared/ui/dtable';
import { EmptyState } from '@shared/ui/EmptyState';
import { ErrorBanner } from '@shared/ui/ErrorBanner';
import { ArrowDownIcon, ArrowUpIcon, EditIcon, PlusIcon, TrashIcon } from '@shared/ui/icons';
import { RowMenu, type RowMenuEntry } from '@shared/ui/rowmenu';
import { useToast } from '@shared/ui/toast/ToastProvider';

import { copy } from './copy';
import { errors } from './errors';
import { useCreateShelf, useDeleteShelf, useReorderShelves, useUpdateShelf } from './mutations';
import { shelvesKey, useCategories, useShelves, useTags } from './queries';
import { ShelfDialog } from './Shelves.ShelfDialog';
import { isViewerScoped, lookup, paramKindFor, selfResolvingNote, sourceLabel } from './Shelves.vocabulary';
import type { CategoryNode, Shelf, ShelfDraft, ShelfSurface } from './types';

const TABLE_COLUMNS = 4;

/** The one layout whose shelf *is* the top of the page, so it has nowhere to link. */
const HERO_LAYOUT = 'hero_slider';

/**
 * One home composition: its ordered shelf list, its create action, and the two
 * dialogs the list drives.
 *
 * **Rank is a field, so there is no column sorting.** The order *is* the data —
 * the catalog calls it «an ordered list of configured shelves» — and a sort
 * control would promise a reordering this surface does not own while
 * contradicting the move handles beside it. The handles are the reading of that
 * row together with «management includes its order».
 *
 * **The handles are disabled at the ends rather than hidden**, so the column
 * does not change width between rows and a keyboard user never loses a focus
 * target mid-operation.
 *
 * **Every destructive action lives in the row menu, never in the row**, and the
 * confirmation names the shelf *and* its surface: a title can appear in both
 * home lists, and deleting one is not deleting the other.
 */
export function SurfaceSection({ surface }: { surface: ShelfSurface }) {
  const query = useShelves(surface);
  const categories = useCategories();
  const tags = useTags();
  const queryClient = useQueryClient();
  const toast = useToast();

  const createShelf = useCreateShelf(surface);
  const updateShelf = useUpdateShelf(surface);
  const deleteShelf = useDeleteShelf(surface);
  const reorderShelves = useReorderShelves(surface);

  const [editing, setEditing] = useState<{ open: boolean; shelf: Shelf | null }>({
    open: false,
    shelf: null,
  });
  const [pendingDelete, setPendingDelete] = useState<{ shelf: Shelf; subject: ConfirmSubject } | null>(null);

  // The handle that moved keeps focus. Rows are keyed by id, so React reuses
  // the DOM node across a reorder and the browser leaves focus wherever it was
  // — which after a move is a handle now sitting on a different row. Focus is
  // therefore restored imperatively once the new order has rendered.
  const sectionRef = useRef<HTMLElement>(null);
  const focusAfterMove = useRef<{ shelfId: string; direction: 'up' | 'down' } | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const headingId = `shelves-${surface}`;
  const surfaceCopy = copy.surfaces[surface];

  const shelves = useMemo(
    () => [...(query.data?.items ?? [])].sort((a, b) => a.position - b.position),
    [query.data],
  );

  /**
   * The two single-valued parameter kinds are stored as identifiers, so the
   * cell resolves them against the vocabularies that own the words. An
   * unresolved identifier renders **as itself** rather than as an empty cell: a
   * visible degradation an operator can act on beats a silent one they cannot.
   */
  const termLabels = useMemo(() => {
    const map = new Map<string, string>();

    function walk(nodes: readonly CategoryNode[]) {
      for (const node of nodes) {
        map.set(node.id, node.name);
        if (node.children?.length) walk(node.children);
      }
    }

    walk(categories.data?.items ?? []);
    for (const tag of tags.data?.items ?? []) map.set(tag.id, tag.name);
    return map;
  }, [categories.data, tags.data]);

  /**
   * A row that reached an end has its handle disabled, and focus must not be
   * left on a disabled control — so it lands on the opposite handle of the same
   * row, which is where the reader's next press would go anyway.
   */
  useEffect(() => {
    const pending = focusAfterMove.current;
    const section = sectionRef.current;
    if (!pending || !section) return;

    focusAfterMove.current = null;

    const opposite = pending.direction === 'up' ? 'down' : 'up';
    const moved = section.querySelector<HTMLButtonElement>(
      `[data-move-handle="${pending.shelfId}:${pending.direction}"]`,
    );
    const fallback = section.querySelector<HTMLButtonElement>(
      `[data-move-handle="${pending.shelfId}:${opposite}"]`,
    );

    (moved && !moved.disabled ? moved : fallback)?.focus();
  }, [shelves]);

  function move(shelf: Shelf, direction: 'up' | 'down') {
    const index = shelves.findIndex((candidate) => candidate.id === shelf.id);
    const target = direction === 'up' ? index - 1 : index + 1;
    if (index < 0 || target < 0 || target >= shelves.length) return;

    const next = [...shelves];
    const [moved] = next.splice(index, 1);
    if (!moved) return;
    next.splice(target, 0, moved);

    focusAfterMove.current = { shelfId: shelf.id, direction };
    setAnnouncement(copy.row.moved(shelf.title, formatNumber(target + 1)));
    reorderShelves.mutate(next.map((candidate) => candidate.id));
  }

  function submitDraft(draft: ShelfDraft) {
    const shelf = editing.shelf;

    if (shelf) {
      updateShelf.mutate(
        { shelfId: shelf.id, draft },
        {
          onSuccess: () => {
            setEditing({ open: false, shelf: null });
            toast.show(copy.toast.saved, 'success');
          },
        },
      );
      return;
    }

    createShelf.mutate(draft, {
      onSuccess: () => {
        setEditing({ open: false, shelf: null });
        toast.show(copy.toast.created, 'success');
      },
    });
  }

  function confirmDelete() {
    const target = pendingDelete;
    if (!target) return;

    deleteShelf.mutate(target.shelf.id, {
      onSuccess: () => {
        setPendingDelete(null);
        toast.show(copy.toast.deleted, 'success');
      },
      onError: (error) => {
        setPendingDelete(null);
        const status = error instanceof ApiError ? error.status : 0;
        const code = error instanceof ApiError ? error.code : undefined;
        toast.show(resolveErrorCopy(code, status, errors).message, 'danger');
      },
    });
  }

  return (
    <section ref={sectionRef} aria-labelledby={headingId} className="mt-section-gap first:mt-0">
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 id={headingId} className="font-display text-h2 font-bold">
          {surfaceCopy.heading}
        </h2>

        {/*
          Every committing action inside the content column is secondary — the
          top bar's create action is this view's one primary button.
        */}
        <button
          type="button"
          onClick={() => setEditing({ open: true, shelf: null })}
          className="hit-target ms-auto flex items-center gap-2 rounded-sm border border-border-strong bg-surface-raised px-3 text-xs font-semibold text-ink hover:bg-surface-overlay"
        >
          <PlusIcon />
          <span>{copy.add}</span>
        </button>
      </div>

      {/*
        The context line sits above the table rather than beneath it: it says
        what the table manages, and that is read before the rows, not after.
      */}
      <p className="mb-4 mt-3 max-w-measure text-xs text-ink-muted">{surfaceCopy.context}</p>

      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>

      {query.isError ? (
        <SectionError
          error={query.error}
          onRetry={() => void queryClient.invalidateQueries({ queryKey: shelvesKey(surface) })}
        />
      ) : null}

      {query.isPending ? (
        <>
          <p role="status" className="sr-only">
            {copy.states.loading}
          </p>
          <ShelfTable caption={surfaceCopy.caption}>
            <RowSkeleton columns={TABLE_COLUMNS} />
          </ShelfTable>
        </>
      ) : null}

      {query.data ? (
        shelves.length === 0 ? (
          <EmptyState message={copy.states.empty} />
        ) : (
          <>
            <ShelfTable caption={surfaceCopy.caption}>
              {shelves.map((shelf, index) => (
                <ShelfRow
                  key={shelf.id}
                  shelf={shelf}
                  position={index + 1}
                  surfaceHeading={surfaceCopy.heading}
                  termLabels={termLabels}
                  first={index === 0}
                  last={index === shelves.length - 1}
                  busy={reorderShelves.isPending}
                  onMove={(direction) => move(shelf, direction)}
                  onEdit={() => setEditing({ open: true, shelf })}
                  onDelete={(subject) => setPendingDelete({ shelf, subject })}
                />
              ))}
            </ShelfTable>

            {/*
              No catalog row grants this list a pager, so it states its own
              truncation instead. Silent truncation is the thing forbidden, not
              truncation: a table showing some of its rows without saying so
              reads as a complete list.
            */}
            <p className="mb-0 mt-3 max-w-measure text-xs text-ink-muted">
              {copy.states.truncation(formatNumber(shelves.length), formatNumber(query.data.total))}
            </p>
          </>
        )
      ) : null}

      <ShelfDialog
        open={editing.open}
        surface={surface}
        shelf={editing.shelf}
        busy={createShelf.isPending || updateShelf.isPending}
        error={editing.shelf ? updateShelf.error : createShelf.error}
        onSubmit={submitDraft}
        onClose={() => setEditing({ open: false, shelf: null })}
      />

      {/* One dialog per table, not one per row — the copy rides the trigger. */}
      <ConfirmDialog
        open={pendingDelete !== null}
        subject={pendingDelete?.subject ?? null}
        busy={deleteShelf.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </section>
  );
}

function SectionError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : 0;
  const code = error instanceof ApiError ? error.code : undefined;
  const resolved = resolveErrorCopy(code, status, errors);

  return (
    <ErrorBanner
      message={resolved.message}
      tone={resolved.tone === 'danger' ? 'danger' : 'neutral'}
      onRetry={onRetry}
    />
  );
}

/**
 * Four columns, and a minimum inline size so the two two-line columns are not
 * crushed — `30rem` squeezed them together. The table scrolls inside its own
 * wrapper and the page body never scrolls horizontally.
 */
function ShelfTable({ caption, children }: { caption: string; children: ReactNode }) {
  return (
    <DTableScroll>
      <DTable caption={caption} minInlineSize="34rem">
        <DTableHead>
          <tr>
            <th scope="col" className="[inline-size:8.5rem]">
              {copy.columns.order}
            </th>
            <th scope="col">{copy.columns.shelf}</th>
            <th scope="col">{copy.columns.source}</th>
            <th scope="col" className="whitespace-nowrap [inline-size:3.5rem]">
              <span className="sr-only">{copy.columns.actions}</span>
            </th>
          </tr>
        </DTableHead>
        <DTableBody>{children}</DTableBody>
      </DTable>
    </DTableScroll>
  );
}

function ShelfRow({
  shelf,
  position,
  surfaceHeading,
  termLabels,
  first,
  last,
  busy,
  onMove,
  onEdit,
  onDelete,
}: {
  shelf: Shelf;
  position: number;
  surfaceHeading: string;
  termLabels: ReadonlyMap<string, string>;
  first: boolean;
  last: boolean;
  busy: boolean;
  onMove: (direction: 'up' | 'down') => void;
  onEdit: () => void;
  onDelete: (subject: ConfirmSubject) => void;
}) {
  const confirmSubject: ConfirmSubject = {
    heading: copy.confirm.heading(shelf.title, surfaceHeading),
    body: copy.confirm.body,
    commitLabel: copy.confirm.commit,
    intent: 'danger',
  };

  const entries: readonly RowMenuEntry[] = [
    {
      key: `${shelf.id}:edit`,
      label: copy.row.edit,
      icon: <EditIcon />,
      onSelect: onEdit,
    },
    {
      key: `${shelf.id}:delete`,
      label: copy.row.delete,
      icon: <TrashIcon />,
      danger: true,
      // The entry does not carry the dialog. `RowMenu` closes itself and then
      // runs `onSelect`, and the page owns the one `ConfirmDialog` every row
      // shares — a confirmation declared per entry would mount one dialog per
      // row and leave the panel standing behind its own backdrop.
      onSelect: () => onDelete(confirmSubject),
    },
  ];

  return (
    <tr>
      <td>
        <span className="flex items-center gap-2">
          <b className="font-semibold [font-variant-numeric:tabular-nums] [min-inline-size:1.25rem]">
            <bdi>{formatNumber(position)}</bdi>
          </b>

          {/*
            The handles stay below the 2.75rem minimum on the pointer tier: the
            real target is the cell that holds them, which is already wider, and
            enlarging them turns every row into two lines. They grow on the
            phone, where there is neither a pointer nor an expanded hit area.
          */}
          <span className="flex gap-1">
            <MoveButton
              handle={`${shelf.id}:up`}
              direction="up"
              label={copy.row.moveUp(shelf.title)}
              disabled={first || busy}
              onClick={() => onMove('up')}
            />
            <MoveButton
              handle={`${shelf.id}:down`}
              direction="down"
              label={copy.row.moveDown(shelf.title)}
              disabled={last || busy}
              onClick={() => onMove('down')}
            />
          </span>
        </span>
      </td>

      <DTableRowHeader>
        <span className="block">{shelf.title}</span>
        <span className="mt-1 block text-xs text-ink-muted">
          {isViewerScoped(shelf.source) ? (
            <>
              {copy.viewerScoped.note}{' '}
              <span className="rounded-pill border border-border bg-surface-raised px-2 py-1">
                {copy.viewerScoped.badge}
              </span>
            </>
          ) : (
            seeAllNote(shelf)
          )}
        </span>
      </DTableRowHeader>

      <td>
        <span className="block">{sourceLabel(shelf.source)}</span>
        {/*
          The second line is always present, even when there is no parameter: a
          silent cell reads as «forgotten» rather than as «needs nothing».
        */}
        <SourceParamNote shelf={shelf} termLabels={termLabels} />
      </td>

      <td>
        <RowMenu label={copy.row.menu(shelf.title)} entries={entries} />
      </td>
    </tr>
  );
}

function MoveButton({
  handle,
  direction,
  label,
  disabled,
  onClick,
}: {
  /** `<shelfId>:<direction>` — what the section's focus-restore effect looks up. */
  handle: string;
  direction: 'up' | 'down';
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  const Glyph = direction === 'up' ? ArrowUpIcon : ArrowDownIcon;

  return (
    <button
      type="button"
      data-move-handle={handle}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex items-center justify-center rounded-sm text-ink-muted [inline-size:2.25rem] [min-block-size:2.25rem] hover:bg-hover-bg hover:text-ink disabled:cursor-not-allowed disabled:opacity-30 md:[inline-size:2rem] md:[min-block-size:2rem]"
    >
      <Glyph />
    </button>
  );
}

/** Where «عرض الكلّ» leads — read off the row, never authored on this surface. */
function seeAllNote(shelf: Shelf): string {
  if (!shelf.seeAllUrl) {
    return shelf.layout === HERO_LAYOUT ? copy.seeAll.heroNone : copy.seeAll.none;
  }
  return lookup(copy.seeAll, shelf.source) ?? copy.seeAll.default;
}

/** What the source resolves against, or a statement that it resolves on its own. */
function SourceParamNote({ shelf, termLabels }: { shelf: Shelf; termLabels: ReadonlyMap<string, string> }) {
  const kind = paramKindFor(shelf.source);
  const values = shelf.sourceParam?.values ?? [];
  const className = 'mt-1 block text-xs text-ink-muted';

  if (!kind) return <span className={className}>{selfResolvingNote(shelf.source)}</span>;

  // A hand-picked set is summarised by its count: eight titles in a table cell
  // are not read, and the count is a figure a human reads.
  if (kind === 'CONTENT_ITEM_SET') {
    return (
      <span className={className}>
        <bdi>{copy.handPicked(formatNumber(values.length))}</bdi>
      </span>
    );
  }

  const id = values[0];
  if (!id) return <span className={className}>—</span>;

  const label = termLabels.get(id);
  return <span className={className}>{label ?? <bdi className="mono">{id}</bdi>}</span>;
}
